import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";
import type { LiveComment, SiteVideoPublic, StreamSource } from "@tih/shared";
import {
  ApiError,
  fetchVideo,
  fetchVideoComments,
  postVideoComment,
} from "../api/client";
import { StreamPlayer } from "../components/StreamPlayer";
import { useAuth } from "../context/AuthContext";

function parseSeconds(raw: string | null): number | undefined {
  if (raw == null || raw.trim() === "") return undefined;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return Math.floor(n);
}

function formatWhen(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function formatCommentTime(iso: string): string {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const sec = Math.round(diff / 1000);
  if (sec < 10) return "just now";
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase() || "?";
}

function formatCount(n: number): string {
  return n.toLocaleString();
}

const MAX_LEN = 1000;

export function VideoWatchPage() {
  const { videoId = "" } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { user } = useAuth();
  const startSeconds = parseSeconds(searchParams.get("start"));
  const endSeconds = parseSeconds(searchParams.get("end"));

  const [video, setVideo] = useState<SiteVideoPublic | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const [comments, setComments] = useState<LiveComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [commentError, setCommentError] = useState<string | null>(null);
  const [pendingNote, setPendingNote] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
    setNotFound(false);
    setVideo(null);
    setComments([]);
    setPendingNote(null);
    setCommentError(null);
    if (!videoId.trim()) {
      setNotFound(true);
      return;
    }
    fetchVideo(videoId)
      .then(setVideo)
      .catch((e) => {
        if (e instanceof ApiError && e.status === 404) setNotFound(true);
        else setError(e instanceof ApiError ? e.message : String(e?.message ?? e));
      });
  }, [videoId]);

  useEffect(() => {
    if (!video?.videoId) return;
    let cancelled = false;
    setCommentsLoading(true);
    fetchVideoComments(video.videoId)
      .then((next) => {
        if (!cancelled) setComments(next);
      })
      .catch(() => {
        if (!cancelled) setComments([]);
      })
      .finally(() => {
        if (!cancelled) setCommentsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [video?.videoId]);

  const source: StreamSource | null = useMemo(() => {
    if (!video) return null;
    return {
      kind: "youtube",
      src: video.videoId,
      poster: video.thumbnailUrl,
    };
  }, [video]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!user || !video) return;
    const trimmed = body.trim();
    if (!trimmed) return;
    setBusy(true);
    setCommentError(null);
    setPendingNote(null);
    try {
      await postVideoComment(video.videoId, { body: trimmed });
      setBody("");
      setPendingNote(
        "Thank you — your comment will appear after a moderator approves it.",
      );
    } catch (err) {
      setCommentError(
        err instanceof ApiError ? err.message : "Could not post your comment",
      );
    } finally {
      setBusy(false);
    }
  }

  if (notFound) {
    return (
      <section className="section video-watch" style={{ textAlign: "center" }}>
        <h2 className="sec-title">Video not found</h2>
        <p style={{ opacity: 0.75, marginBottom: 32 }}>
          That video may be unpublished or the link is incorrect.
        </p>
        <Link to="/videos" className="btn btn-primary">
          Back to videos
        </Link>
      </section>
    );
  }

  if (error) {
    return (
      <section className="section video-watch">
        <div className="form-error">{error}</div>
      </section>
    );
  }

  if (!video || !source) {
    return (
      <section className="section video-watch">
        <div className="page-empty" aria-busy="true">
          Loading…
        </div>
      </section>
    );
  }

  const eventLabel =
    video.event?.title?.trim() ||
    (video.event ? formatWhen(video.event.startsAt) : null);

  return (
    <section className="section video-watch">
      <div className="video-watch-breadcrumb">
        <Link to="/videos">Videos</Link>
        {video.category && (
          <>
            <span aria-hidden="true"> / </span>
            <Link to={`/videos?category=${encodeURIComponent(video.category)}`}>
              {video.category}
            </Link>
          </>
        )}
      </div>

      <div className="video-watch-player">
        <StreamPlayer
          key={`${source.src}:${startSeconds ?? ""}:${endSeconds ?? ""}`}
          source={source}
          poster={video.thumbnailUrl}
          title={video.title}
          startSeconds={startSeconds}
          endSeconds={endSeconds}
        />
      </div>

      <header className="video-watch-header">
        <h1 className="video-watch-title">{video.title}</h1>
        <div className="video-watch-meta">
          {video.publishedAt && (
            <time dateTime={video.publishedAt}>{formatWhen(video.publishedAt)}</time>
          )}
          {video.category && <span className="video-watch-category">{video.category}</span>}
          {video.event && eventLabel && (
            <span className="video-watch-event">
              From <Link to="/event">{eventLabel}</Link>
            </span>
          )}
          <span>
            {formatCount(video.viewCount)} views · {formatCount(video.likeCount)} likes ·{" "}
            {formatCount(video.commentCount)} comments
          </span>
        </div>
        {video.description && <p className="video-watch-description">{video.description}</p>}
        {video.tags.length > 0 && (
          <ul className="video-watch-tags">
            {video.tags.map((t) => (
              <li key={t}>{t}</li>
            ))}
          </ul>
        )}
      </header>

      <section className="live-comments video-watch-comments" aria-label="Comments">
        <header className="live-comments-head">
          <h2>
            {comments.length} {comments.length === 1 ? "Comment" : "Comments"}
          </h2>
        </header>

        {user ? (
          <form className="live-comment-form" onSubmit={onSubmit}>
            <div className="lc-avatar" aria-hidden="true">
              {initials(user.name)}
            </div>
            <div className="lc-form-body">
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                maxLength={MAX_LEN}
                rows={2}
                placeholder="Add a comment…"
                disabled={busy}
              />
              <div className="lc-form-row">
                <span className="lc-count">
                  {body.length}/{MAX_LEN}
                </span>
                <div className="lc-form-actions">
                  <button
                    type="button"
                    className="btn btn-ghost"
                    disabled={busy || !body}
                    onClick={() => setBody("")}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={busy || !body.trim()}
                  >
                    {busy ? "Posting…" : "Comment"}
                  </button>
                </div>
              </div>
              {commentError && <div className="form-error">{commentError}</div>}
              {pendingNote && (
                <div className="form-success" style={{ padding: 10 }}>
                  {pendingNote}
                </div>
              )}
            </div>
          </form>
        ) : (
          <div className="live-comment-signin">
            <Link
              to={`/sign-in?next=${encodeURIComponent(location.pathname + location.search)}`}
              className="btn btn-primary"
            >
              Sign in to comment
            </Link>
            <span className="lc-signin-hint">
              Share a verse, a prayer, or a hello — comments are moderated before they appear.
            </span>
          </div>
        )}

        {commentsLoading ? (
          <div className="live-comments-empty">Loading comments…</div>
        ) : comments.length === 0 ? (
          <div className="live-comments-empty">
            No comments yet. Be the first to leave one.
          </div>
        ) : (
          <ul className="live-comment-list">
            {comments.map((c) => (
              <li className="live-comment" key={c.id}>
                <div className="lc-avatar" aria-hidden="true">
                  {initials(c.authorName)}
                </div>
                <div className="lc-body">
                  <div className="lc-meta">
                    <span className="lc-author">{c.authorName}</span>
                    {c.source === "YOUTUBE" ? (
                      <span className="lc-source">YouTube</span>
                    ) : (
                      <span className="lc-source lc-source--site">Site</span>
                    )}
                    <span className="lc-time">{formatCommentTime(c.createdAt)}</span>
                  </div>
                  <p className="lc-text">{c.body}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </section>
  );
}
